import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import {
  Heart,
  Trophy,
  Users,
  TrendingUp,
  Quote,
  ChevronRight,
  CheckCircle,
  Target,
  Sparkles,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { BACKEND_URL } from '../utils/config';

const SuccessStoriesPage = () => {
  const { t } = useLanguage();
  const [successCampaigns, setSuccessCampaigns] = useState([]);
  const [featuredStories, setFeaturedStories] = useState([]);
  const [stats, setStats] = useState({
    totalRaised: 0,
    successfulCampaigns: 0,
    totalDonors: 0,
    successRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSuccessData();
  }, []);

  const fetchSuccessData = async () => {
    try {
      // Fetch all campaigns
      const res = await fetch(`${BACKEND_URL}/api/campaigns?limit=50`);
      const allCampaigns = await res.json();
      
      // Calculate real stats
      let totalRaised = 0;
      let totalDonors = 0;
      let successfulCount = 0;
      
      allCampaigns.forEach(c => {
        totalRaised += c.raised || 0;
        totalDonors += c.donors || 0;
        if ((c.raised / c.goal) >= 0.5) {
          successfulCount++;
        }
      });
      
      const successRate = allCampaigns.length > 0 
        ? Math.round((successfulCount / allCampaigns.length) * 100) 
        : 0;
      
      setStats({
        totalRaised,
        successfulCampaigns: successfulCount,
        totalDonors,
        successRate
      });
      
      // Get highly funded campaigns for featured stories (>50% funded)
      const highlyFunded = allCampaigns
        .filter(c => (c.raised / c.goal) >= 0.5)
        .sort((a, b) => (b.raised / b.goal) - (a.raised / a.goal))
        .slice(0, 3);
      
      setFeaturedStories(highlyFunded);
      
      // Get recently successful campaigns for the grid
      const recentSuccessful = allCampaigns
        .filter(c => (c.raised / c.goal) >= 0.3)
        .sort((a, b) => b.raised - a.raised)
        .slice(0, 6);
      
      setSuccessCampaigns(recentSuccessful);
      
    } catch (err) {
      console.error('Failed to fetch success stories:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K+`;
    }
    return num.toLocaleString();
  };

  // Dynamic stats based on real data
  const displayStats = [
    { icon: Trophy, value: formatCurrency(stats.totalRaised), label: "Total Raised", color: "text-yellow-500" },
    { icon: CheckCircle, value: stats.successfulCampaigns.toString(), label: "Successful Campaigns", color: "text-emerald-500" },
    { icon: Users, value: formatNumber(stats.totalDonors), label: "Happy Donors", color: "text-blue-500" },
    { icon: Target, value: `${stats.successRate}%`, label: "Success Rate", color: "text-purple-500" }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-emerald-900 via-teal-900 to-emerald-900 py-20 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-teal-900/90"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 mb-6">
              <Sparkles className="w-4 h-4 mr-1" /> {t('realImpactStories')}
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              {t('successStoriesHeroTitle')} <span className="text-emerald-400">{t('successStoriesHighlight')}</span>
            </h1>
            <p className="text-xl text-emerald-100/80 max-w-3xl mx-auto mb-10">
              {t('successStoriesHeroDesc')}
            </p>
            
            {/* Real Stats from Database */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {displayStats.map((stat, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <stat.icon className={`w-8 h-8 ${stat.color} mx-auto mb-3`} />
                  <div className="text-3xl font-bold text-foreground mb-1">{stat.value}</div>
                  <div className="text-sm text-emerald-200/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Success Stories - Real Campaigns */}
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t('featuredSuccessStories').split(' ')[0]} <span className="text-emerald-500">{t('featuredSuccessStories').split(' ').slice(1).join(' ')}</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('featuredStoriesDesc')}
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
              </div>
            ) : featuredStories.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-8">
                {featuredStories.map((campaign) => {
                  const progress = Math.min((campaign.raised / campaign.goal) * 100, 100);
                  return (
                    <Card key={campaign.id} className="bg-card border-border overflow-hidden group hover:shadow-xl transition-all duration-300">
                      <CardContent className="p-0">
                        <div className="relative h-48 overflow-hidden">
                          <img 
                            src={campaign.image} 
                            alt={campaign.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute top-3 right-3">
                            <Badge className="bg-emerald-500 text-white border-none">
                              {Math.round(progress)}% Funded
                            </Badge>
                          </div>
                          <div className="absolute bottom-3 left-3">
                            <Badge variant="secondary" className="bg-card/90 capitalize">
                              {campaign.category}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="p-6">
                          <h3 className="font-semibold text-lg text-foreground mb-2 line-clamp-2">
                            {campaign.title}
                          </h3>
                          
                          <div className="flex items-center gap-3 mb-4">
                            <img 
                              src={campaign.creator?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'} 
                              alt={campaign.creator?.name}
                              className="w-10 h-10 rounded-full border-2 border-emerald-500"
                            />
                            <div>
                              <p className="text-sm font-medium text-foreground">{campaign.creator?.name}</p>
                              <p className="text-xs text-muted-foreground">Campaign Creator</p>
                            </div>
                          </div>
                          
                          <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                            {campaign.story?.substring(0, 150)}...
                          </p>
                          
                          <div className="flex items-center justify-between pt-4 border-t border-border">
                            <div>
                              <p className="text-xs text-muted-foreground">Raised</p>
                              <p className="text-lg font-bold text-emerald-500">{formatCurrency(campaign.raised)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Supporters</p>
                              <p className="text-sm font-medium text-foreground">{campaign.donors?.toLocaleString() || 0}</p>
                            </div>
                          </div>
                          
                          {progress >= 100 && (
                            <div className="mt-4 flex items-center gap-2 text-sm text-emerald-500">
                              <TrendingUp className="w-4 h-4" />
                              <span>Goal Exceeded!</span>
                            </div>
                          )}
                          
                          <Link to={`/campaign/${campaign.id}`} className="block mt-4">
                            <Button variant="outline" className="w-full border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:text-white">
                              View Campaign <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-12">
                No featured success stories yet. Be the first to create a successful campaign!
              </p>
            )}
          </div>
        </section>

        {/* All Success Campaigns Grid */}
        <section className="py-20 bg-secondary/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                More <span className="text-emerald-500">Inspiring</span> Campaigns
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Every campaign tells a story of hope, community, and the power of giving.
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {successCampaigns.map((campaign) => {
                  const progress = Math.min((campaign.raised / campaign.goal) * 100, 100);
                  return (
                    <Link 
                      key={campaign.id} 
                      to={`/campaign/${campaign.id}`}
                      className="group"
                    >
                      <Card className="bg-card border-border overflow-hidden hover:shadow-lg transition-all duration-300 h-full">
                        <div className="relative h-48 overflow-hidden">
                          <img 
                            src={campaign.image} 
                            alt={campaign.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          {progress >= 100 && (
                            <div className="absolute top-3 left-3">
                              <Badge className="bg-emerald-500 text-white border-none">
                                <CheckCircle className="w-3 h-3 mr-1" /> Fully Funded
                              </Badge>
                            </div>
                          )}
                          <div className="absolute top-3 right-3">
                            <Badge variant="secondary" className="bg-card/90 capitalize">
                              {campaign.category}
                            </Badge>
                          </div>
                        </div>
                        
                        <CardContent className="p-5">
                          <h3 className="font-semibold text-foreground group-hover:text-emerald-500 transition-colors line-clamp-2 mb-3">
                            {campaign.title}
                          </h3>
                          
                          {/* Creator info */}
                          <div className="flex items-center gap-2 mb-3">
                            <img 
                              src={campaign.creator?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'} 
                              alt={campaign.creator?.name}
                              className="w-6 h-6 rounded-full"
                            />
                            <span className="text-sm text-muted-foreground">by {campaign.creator?.name}</span>
                          </div>
                          
                          {/* Progress Bar */}
                          <div className="mb-4">
                            <div className="flex justify-between text-sm mb-2">
                              <span className="font-bold text-emerald-500">{formatCurrency(campaign.raised)}</span>
                              <span className="text-muted-foreground">of {formatCurrency(campaign.goal)}</span>
                            </div>
                            <div className="h-2 bg-secondary rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {campaign.donors?.toLocaleString() || 0} donors
                            </span>
                            <span className="font-medium text-emerald-500">{Math.round(progress)}% funded</span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}

            <div className="text-center mt-12">
              <Link to="/discover">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600">
                  Explore All Campaigns <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Heart className="w-16 h-16 text-white/80 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to Create Your Success Story?
            </h2>
            <p className="text-xl text-emerald-100 mb-8">
              Join thousands of successful campaigners who turned their dreams into reality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create">
                <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50 w-full sm:w-auto">
                  Start Your Campaign <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <Link to="/discover">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
                  Support a Cause
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SuccessStoriesPage;
